import { useState, useEffect, useRef } from 'react';
import { TCustomer } from '@/types/TSmartCRM';
import { createBulkLeads, createLead, deleteLead, getLeads, updateLead } from '@/api/crm/smartCRM';
import {
	createInitialPaginationState,
	resetPaginationState,
	updatePaginationFromResponse,
} from '@/utils/SmartCRM/paginationUtils';

export const useLeads = (agentId?: string) => {
	const [customers, setCustomers] = useState<TCustomer[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [pagination, setPagination] = useState(createInitialPaginationState());

	const isInitializing = useRef(false);
	const lastAgentId = useRef<string>('');

	const fetchLeads = async (pageSize = 10, lastEvaluatedKey: string | null = null) => {
		if (!agentId) {
			return;
		}

		setLoading(true);
		try {
			const response = await getLeads({
				agentId,
				includeVariables: 'true',
				pagination: { limit: pageSize, lastEvaluatedKey },
				sortBy: 'createdAt',
				sortOrder: 'desc',
			});

			if (response.success) {
				setCustomers(response.data?.leads || []);
				setPagination((prev) =>
					updatePaginationFromResponse(prev, response.data?.pagination, pageSize),
				);
			}
		} catch (err) {
			console.error('fetchLeads error:', err);
		} finally {
			setLoading(false);
		}
	};

	const fetchFilteredLeads = async (
		filterGroups: any,
		pageSize = 10,
		lastEvaluatedKey: string | null = null,
	) => {
		if (!agentId) {
			return;
		}

		setLoading(true);
		try {
			// filterGroups should already be in backend format from FilterBuilder
			const serializedFilterGroups = JSON.stringify(filterGroups);

			const response = await getLeads({
				agentId,
				includeVariables: 'true',
				filterGroups: serializedFilterGroups,
				pagination: { limit: pageSize, lastEvaluatedKey },
				sortBy: 'createdAt',
				sortOrder: 'desc',
			});

			if (response.success && response.data?.groups) {
				// Backend returns groups object, flatten to get all leads
				const allLeads = Object.values(response.data.groups).flat();
				return {
					leads: allLeads,
					pagination: response.data?.pagination,
					count: allLeads.length,
				};
			}
		} catch (err) {
			console.error('fetchFilteredLeads error:', err);
			throw err;
		} finally {
			setLoading(false);
		}
		return null;
	};

	const nextPage = () => {
		if (pagination.hasNext && pagination.nextKey) {
			const currentKey =
				pagination.prevKeys.length > 0 ? pagination.prevKeys[pagination.prevKeys.length - 1] : null;

			setPagination((prev) => ({
				...prev,
				prevKeys: [...prev.prevKeys, currentKey],
			}));

			fetchLeads(pagination.pageSize, pagination.nextKey);
		}
	};

	const prevPage = () => {
		if (pagination.prevKeys.length > 0) {
			const newPrevKeys = [...pagination.prevKeys];
			const prevKey = newPrevKeys.pop();
			setPagination((prev) => ({ ...prev, prevKeys: newPrevKeys }));
			fetchLeads(pagination.pageSize, prevKey || null);
		}
	};

	const changePageSize = (newSize: number) => {
		setPagination((prev) => ({ ...prev, pageSize: newSize, prevKeys: [] }));
		fetchLeads(newSize, null);
	};

	const resetPagination = () => {
		setPagination((prev) => resetPaginationState(prev));
	};

	const addCustomer = async (customerData: any) => {
		if (!agentId) {
			throw new Error('Agent ID required');
		}

		const leadData = {
			agentId,
			name: customerData.name,
			email: customerData.email,
			number: customerData.number,
			variables: customerData.variables || {},
		};

		const response = await createLead(leadData);
		if (response.success) {
			setPagination((prev) => ({ ...prev, prevKeys: [] }));
			fetchLeads(pagination.pageSize, null);
			return response.data.newLead;
		}
		throw new Error('Failed to create lead');
	};

	const editCustomer = async (customerId: string, updates: any) => {
		if (!agentId) {
			throw new Error('Agent ID required');
		}

		const leadData = {
			agentId,
			name: updates.name ? String(updates.name) : undefined,
			email: updates.email ? String(updates.email) : undefined,
			variables: updates.variables || {},
		};

		Object.keys(leadData).forEach((key) => {
			if (leadData[key] === undefined) {
				delete leadData[key];
			}
		});

		const response = await updateLead(customerId, leadData);
		if (response.success) {
			const currentKey =
				pagination.prevKeys.length > 0 ? pagination.prevKeys[pagination.prevKeys.length - 1] : null;
			fetchLeads(pagination.pageSize, currentKey);
		} else {
			throw new Error('Failed to update lead');
		}
	};

	// Enhanced removeCustomer function to support variable deletion
	const removeCustomer = async (customerId: string, variableId?: string) => {
		const response = await deleteLead(customerId, variableId);
		if (response.success) {
			const currentKey =
				pagination.prevKeys.length > 0 ? pagination.prevKeys[pagination.prevKeys.length - 1] : null;
			fetchLeads(pagination.pageSize, currentKey);
		} else {
			throw new Error(variableId ? 'Failed to delete variable' : 'Failed to delete lead');
		}
	};

	const addBulkCustomers = async (
		customersData: Array<{
			name: string;
			email: string;
			number: string;
			variables?: { [key: string]: any };
		}>,
	) => {
		if (!agentId) {
			throw new Error('Agent ID required');
		}

		const bulkData = {
			agentId,
			leads: customersData,
		};

		const response = await createBulkLeads(bulkData);
		if (response.success) {
			setPagination((prev) => ({ ...prev, prevKeys: [] }));
			fetchLeads(pagination.pageSize, null);
			return {
				createdLeads: response.data.createdLeads,
				failedLeads: response.data.failedLeads || [],
			};
		}
		throw new Error('Failed to create bulk leads');
	};

	useEffect(() => {
		if (!agentId || isInitializing.current || lastAgentId.current === agentId) {
			return;
		}

		isInitializing.current = true;
		lastAgentId.current = agentId;

		fetchLeads(pagination.pageSize, null).finally(() => {
			isInitializing.current = false;
		});
	}, [agentId]);

	return {
		customers,
		loading,
		pagination: {
			...pagination,
			nextPage,
			prevPage,
			changePageSize,
		},
		createCustomer: addCustomer,
		updateCustomer: editCustomer,
		deleteCustomer: removeCustomer,
		resetPagination,
		fetchLeads,
		fetchFilteredLeads,
		createBulkCustomers: addBulkCustomers,
	};
};
